import { DatarouterError } from '@zg-rentals/web-base';
import { LoadingStates, useRentalManagerUser, useRouter, useStaticApp } from '@zg-rentals/rental-manager-env';
import {
  Banner,
  Box,
  Button,
  ButtonGroup,
  CloseButton,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Modal,
  Paragraph,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import React from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { manifest } from '../../app/routes';
import { useAppGuestPay } from '../../hooks/useGuestPay';
import {
  AdproductsGuestBillingInvoiceApiHandler_getInvoicesByNumberAndAccountForGuest,
  AdproductsUserBillingInvoiceApiHandler_getInvoicesByNumberAndAccount,
} from '@zg-rentals/ts-adproducts-client';
import {
  emitGuestPayLandingContinueEvent,
  emitGuestPayLandingHelpModalOpenEvent,
  emitGuestPayLandingSignInEvent,
} from '../../model/GuestPayClickstreamEvents';

// Replaced styled components with Box components using css prop

const validateString = (input: string, regex: RegExp) => {
  if (!input) {
    return {
      isValid: false,
      formatted: '',
    };
  }
  const formatted = input.trim();
  if (regex.test(formatted)) {
    return {
      isValid: true,
      formatted: formatted.toUpperCase(),
    };
  }

  return {
    isValid: false,
    formatted: '',
  };
};

const validateInvoiceNumber = (invoiceNumber: string) => {
  return validateString(invoiceNumber, /inv[0-9]{8}/i);
};

const validateAccountNumber = (invoiceNumber: string) => {
  return validateString(invoiceNumber, /ZRN-[0-9]{7}-ZRN-[0-9]{7}/i);
};

export const GuestPayLandingPage = () => {
  const { updateGuestPayState, guestPayState, initializeGuestPay } = useAppGuestPay();
  const router = useRouter();
  const location = useLocation();
  const initialInvoiceNumber = validateInvoiceNumber(router.query['invoiceNumber']);
  const [showAccessError, setShowAccessError] = React.useState(router.query['accessError'] === 'true');
  const { user } = useRentalManagerUser();
  const { initialAuthComplete } = useStaticApp();
  const [invoiceNumber, setInvoiceNumber] = React.useState(initialInvoiceNumber.formatted);
  const [accountNumber, setAccountNumber] = React.useState('');
  const [email, setEmail] = React.useState(user.loggedIn ? user.email : '');
  const [emailError, setEmailError] = React.useState('');
  const [accountNumberError, setAccountNumberError] = React.useState('');
  const [invoiceNumberError, setInvoiceNumberError] = React.useState('');
  const history = useHistory();
  const [error, setError] = React.useState('');
  const [showHelpModal, setShowHelpModal] = React.useState(false);

  React.useEffect(() => {
    initializeGuestPay();
  }, []);

  const handleUpdateInvoiceNumber = (input: string) => {
    const formatted = input
      .toUpperCase()
      .trim()
      .split('')
      .filter((invoiceCharacter) => /[INV0-9]/.test(invoiceCharacter))
      .slice(0, 12)
      .join('');
    setInvoiceNumber(formatted);
  };

  const handleUpdateAccountNumber = (input: string) => {
    setAccountNumber(input.trim().substring(0, 23));
  };

  const handleGetInvoices = () => {
    setError('');
    let apiCall;
    updateGuestPayState({
      primaryInvoice: invoiceNumber,
      accountNumber: accountNumber,
      guestPayLoadingSessionState: LoadingStates.LOADING,
    });
    if (user.loggedIn) {
      apiCall = AdproductsUserBillingInvoiceApiHandler_getInvoicesByNumberAndAccount({
        invoiceNumber,
        accountNumber,
        guestPayEmail: email,
      });
    } else {
      apiCall = AdproductsGuestBillingInvoiceApiHandler_getInvoicesByNumberAndAccountForGuest({
        invoiceNumber,
        accountNumber,
        guestPayEmail: email,
      });
    }
    apiCall
      .then((result) => {
        updateGuestPayState({
          accountHasAccessToInvoices: result.accountHasAccessToInvoices,
          guestPaySessionKey: result.guestPaySessionKey,
          invoices: result.invoices,
          guestPayLoadingSessionState: LoadingStates.SUCCESS,
          accountNumber: accountNumber,
          primaryInvoice: invoiceNumber,
        });
        const hasOpenInvoices = result.invoices.some((invoice) => invoice.balance > 0);
        emitGuestPayLandingContinueEvent({ user });
        if (hasOpenInvoices) {
          history.push(manifest.guestPayInvoicesPage);
        } else {
          history.push(manifest.guestPayNoInvoicesPage);
        }
        return;
      })
      .catch((e) => {
        setError(DatarouterError.getDisplayMessage(e));
        updateGuestPayState({
          guestPayLoadingSessionState: LoadingStates.ERROR,
        });
      });
  };

  const checkFields = () => {
    let hasErrors = false;
    if (!/(.+?)@(.+?)\.[a-zA-Z]{1,}/.test(email)) {
      setEmailError('Enter a valid email address');
      hasErrors = true;
    } else {
      setEmailError('');
    }
    if (validateInvoiceNumber(invoiceNumber).isValid) {
      setInvoiceNumberError('');
    } else {
      hasErrors = true;
      setInvoiceNumberError('Enter a valid invoice number, ex: INV12345678');
    }
    if (validateAccountNumber(accountNumber).isValid) {
      setAccountNumberError('');
    } else {
      hasErrors = true;
      setAccountNumberError('Enter a valid account number, ex: ZRN-1234567-ZRN-1234567');
    }
    if (!hasErrors) {
      handleGetInvoices();
    }
  };

  const renderAuthenticationWidget = () => {
    const HelpModal = () => (
      <Modal
        open={showHelpModal}
        onOpenChange={(open) => setShowHelpModal(open)}
        size="sm"
        header="Where are my invoice & account numbers?"
        body={
          <Text>
            The invoice number and account number are both at the top of your invoice. The invoice number starts with
            INV, and the account number starts with ZRN.
          </Text>
        }
        footer={
          <ButtonGroup aria-label="Modal actions">
            <Button tone="brand" emphasis="filled" onClick={() => setShowHelpModal(false)}>
              Close
            </Button>
          </ButtonGroup>
        }
      />
    );

    const getLoginLink = () => {
      return `/rental-manager/login/?url=${encodeURIComponent(location.pathname)}`;
    };

    return (
      <>
        <Box
          css={{
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            background: 'white',
          }}
        >
          <Box css={{ maxWidth: '364px', marginBottom: '200px' }}>
            <Box css={{ marginTop: 'xl' }}>
              <Heading level={5} css={{ marginBottom: 'xs', textAlign: 'center' }}>
                Pay as a guest
              </Heading>
              <Paragraph textStyle="body-xs" css={{ marginBottom: 'md', textAlign: 'center', color: 'gray.600' }}>
                If you don&apos;t have an account, or your account differs from the one that received the invoice, you
                can still pay as a guest. All you need are an invoice number and account number.
              </Paragraph>
              {showAccessError && (
                <Box css={{ marginBottom: 'md' }}>
                  <Banner
                    css={{ marginBottom: 'md' }}
                    onClose={() => setShowAccessError(false)}
                    closeButton={<CloseButton />}
                  >
                    Your account doesn&apos;t have access to that invoice -- you can pay as a guest instead.
                  </Banner>
                </Box>
              )}
              {error && (
                <Box css={{ marginBottom: 'md' }}>
                  <Banner
                    css={{ marginBottom: 'md' }}
                    tone="critical"
                    onClose={() => setError('')}
                    closeButton={<CloseButton />}
                  >
                    {error}
                  </Banner>
                </Box>
              )}
              <FormField
                css={{ marginBottom: 'sm' }}
                error={!!invoiceNumberError}
                label={<Label>Invoice number</Label>}
                control={
                  <Input
                    value={invoiceNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateInvoiceNumber(e.target.value)}
                    placeholder="INV12345678"
                  />
                }
                description={invoiceNumberError && <FormHelp>{invoiceNumberError}</FormHelp>}
              />
              <FormField
                error={!!accountNumberError}
                css={{ marginBottom: 'sm' }}
                label={<Label>Account number</Label>}
                control={
                  <Input
                    value={accountNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateAccountNumber(e.target.value)}
                    placeholder="ZRN-1234567-ZRN-1234567"
                  />
                }
                description={accountNumberError && <FormHelp>{accountNumberError}</FormHelp>}
              />
              <FormField
                error={!!emailError}
                css={{ marginBottom: 'sm' }}
                label={<Label>Email address for payment confirmation</Label>}
                control={
                  <Input
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  />
                }
                description={emailError && <FormHelp>{emailError}</FormHelp>}
              />
              <Box css={{ marginBottom: 'xs' }}>
                <Button
                  css={{ width: '100%' }}
                  onClick={checkFields}
                  tone="brand"
                  emphasis="filled"
                  disabled={guestPayState.guestPayLoadingSessionState === LoadingStates.LOADING}
                >
                  {guestPayState.guestPayLoadingSessionState === LoadingStates.LOADING ? 'Loading...' : 'Continue'}
                </Button>
              </Box>
              <TextButton
                onClick={() => {
                  emitGuestPayLandingHelpModalOpenEvent({ user });
                  setShowHelpModal(true);
                }}
              >
                Where are my invoice and account numbers?
              </TextButton>
              {initialAuthComplete && !user.loggedIn && (
                <Box css={{ marginTop: 'xl' }}>
                  <Paragraph textStyle="body-bold" css={{ marginBottom: 'xs', textAlign: 'center' }}>
                    Have an account with invoice access?
                  </Paragraph>
                  <Box css={{ marginBottom: 'xs' }}>
                    <Button css={{ width: '100%' }} tone="brand" emphasis="outlined" asChild>
                      <Link to={getLoginLink()} onClick={() => emitGuestPayLandingSignInEvent({ user })}>
                        Sign in to pay
                      </Link>
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <HelpModal />
      </>
    );
  };

  return <div>{renderAuthenticationWidget()}</div>;
};
